import { useMemo, useState } from 'react';

import toggleProduct from '../helpers/toggleProduct';
import { NormalizedCart } from '../../types';
import { OutgoingMembershipProductDto } from 'api/dtos/memberships/products';

const DEFAULT_MAX_PRODUCTS_PER_TRANSACTION = 1;

/**
 * Creates and manages a cart.
 */
const useCart = () => {
  const [selectedProducts, setSelectedProducts] = useState<NormalizedCart>({});

  const maxProductsPerTransaction = DEFAULT_MAX_PRODUCTS_PER_TRANSACTION;

  /**
   * Calculates the total amount of products selected.
   */
  const totalAmountOfProductsSelected = useMemo(
    () =>
      Object.values(selectedProducts).reduce(
        (total, product) => total + product.metadata.quantity,
        0
      ),
    [selectedProducts]
  );

  /**
   * Toggles the selected product in cart.
   */
  const onToggleProduct = (
    toggledProduct: OutgoingMembershipProductDto,
    setAsync = false
  ): Promise<{
    products: NormalizedCart;
    asyncSetter: () => void | undefined;
  }> =>
    new Promise((resolve) => {
      let products = { ...selectedProducts };

      // As of now, we only allow one product per transaction. So if the user tries to add a second product, we remove the first one.
      // In the future, we will allow multiple products per transaction. Check event checkout for reference.
      if (totalAmountOfProductsSelected >= maxProductsPerTransaction) {
        products = toggleProduct(
          products,
          products[Object.keys(products)[0]].productData
        );
      }

      products = toggleProduct(products, toggledProduct);

      if (setAsync) {
        resolve({
          products,
          asyncSetter: () => setSelectedProducts(products)
        });
      } else {
        setSelectedProducts(products);
        resolve({ products, asyncSetter: undefined });
      }
    });

  const reset = () => {
    setSelectedProducts({});
  };

  return {
    maxProductsPerTransaction,
    selectedProducts,
    isEmpty: Object.keys(selectedProducts).length === 0,
    toggleProduct: onToggleProduct,
    totalAmountOfProductsSelected,
    reset
  };
};

export default useCart;
